import React, { useState, useRef } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, formatDKK, formatApiError } from "@/lib/api";
import { getAttribution } from "@/lib/attribution";
import { trackLeadSuccess } from "@/lib/analytics";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowLeft, ArrowRight } from "lucide-react";

const TIME_OPTS = ["any", "morning", "afternoon", "evening"];

export default function BookingDialog({ open, onOpenChange, serviceType, configuration = {}, fileIds = [], priceData = null, postcode = "", source = "manual", recognizedComponents = [], successText = null }) {
  const { t, lang } = useLang();
  const isQuote = serviceType !== "pax";
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const inFlight = useRef(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    postcode: postcode || "", address: "", preferred_date: "", preferred_time: "any",
    comments: "", consent: false,
  });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => { setStep(1); setDone(false); setForm((f) => ({ ...f, consent: false })); };
  const close = () => { onOpenChange(false); setTimeout(reset, 300); };

  const validateStep1 = () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error(t.booking.errName); return false; }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { toast.error(t.booking.errEmail); return false; }
    if (!form.phone.trim()) { toast.error(t.booking.errPhone); return false; }
    if (!form.postcode.trim()) { toast.error(t.booking.errPostcode); return false; }
    return true;
  };

  const submit = async () => {
    if (inFlight.current) return;
    if (!form.consent) { toast.error(t.booking.errConsent); return; }
    inFlight.current = true;
    setSubmitting(true);
    try {
      const res = await api.post("/leads", {
        service_type: serviceType,
        first_name: form.first_name, last_name: form.last_name,
        email: form.email, phone: form.phone, postcode: form.postcode,
        address: form.address, preferred_date: form.preferred_date, preferred_time: form.preferred_time,
        comments: form.comments, consent: form.consent, language: lang,
        configuration, file_ids: fileIds,
        source, recognized_components: recognizedComponents,
        attribution: getAttribution(),
      });
      trackLeadSuccess({ leadId: res.data?.id, serviceType });
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-[#1E3A2F] focus:ring-1 focus:ring-[#1E3A2F] outline-none text-base";
  const labelCls = "block text-sm font-medium text-stone-700 mb-1.5";

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogDescription className="sr-only">{isQuote ? t.booking.quoteTitle : t.booking.title}</DialogDescription>
        {done ? (
          <div className="text-center py-8" data-testid="booking-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h3 className="mt-4 text-xl font-display font-bold text-stone-900">{t.booking.successTitle}</h3>
            <p className="mt-2 text-stone-600">{isQuote ? (successText || t.booking.successQuote) : t.booking.successBooking}</p>
            <button onClick={close} className="mt-6 px-6 py-3 rounded-full bg-[#1E3A2F] text-white font-semibold" data-testid="booking-done-btn">
              {t.booking.done}
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl">{isQuote ? t.booking.quoteTitle : t.booking.title}</DialogTitle>
              <p className="text-sm text-stone-500">{t.booking.step} {step} {t.booking.of} 3</p>
            </DialogHeader>

            {step === 1 && (
              <div className="space-y-4" data-testid="booking-step-contact">
                <h4 className="font-semibold text-stone-800">{t.booking.contactTitle}</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>{t.booking.firstName}</label>
                    <input className={inputCls} value={form.first_name} onChange={(e) => upd("first_name", e.target.value)} data-testid="booking-first-name" />
                  </div>
                  <div>
                    <label className={labelCls}>{t.booking.lastName}</label>
                    <input className={inputCls} value={form.last_name} onChange={(e) => upd("last_name", e.target.value)} data-testid="booking-last-name" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>{t.booking.email}</label>
                  <input type="email" className={inputCls} value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="booking-email" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>{t.booking.phone}</label>
                    <input className={inputCls} value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="booking-phone" />
                  </div>
                  <div>
                    <label className={labelCls}>{t.booking.postcode}</label>
                    <input inputMode="numeric" className={inputCls} value={form.postcode} onChange={(e) => upd("postcode", e.target.value)} data-testid="booking-postcode" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>{t.booking.address} <span className="text-stone-400 font-normal">({t.common.optional})</span></label>
                  <input className={inputCls} value={form.address} onChange={(e) => upd("address", e.target.value)} data-testid="booking-address" />
                  <p className="text-xs text-stone-400 mt-1">{t.booking.addressNote}</p>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4" data-testid="booking-step-date">
                <h4 className="font-semibold text-stone-800">{t.booking.dateTitle}</h4>
                <div>
                  <label className={labelCls}>{t.booking.preferredDate}</label>
                  <input type="date" min={new Date().toISOString().split("T")[0]} className={inputCls} value={form.preferred_date} onChange={(e) => upd("preferred_date", e.target.value)} data-testid="booking-date" />
                </div>
                <div>
                  <label className={labelCls}>{t.booking.timeWindow}</label>
                  <div className="grid grid-cols-2 gap-2">
                    {TIME_OPTS.map((o) => (
                      <button
                        key={o}
                        type="button"
                        onClick={() => upd("preferred_time", o)}
                        className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${form.preferred_time === o ? "border-[#1E3A2F] bg-[#1E3A2F] text-white" : "border-stone-300 text-stone-700 hover:bg-stone-50"}`}
                        data-testid={`booking-time-${o}`}
                      >
                        {t.booking[`time${o.charAt(0).toUpperCase() + o.slice(1)}`] || t.booking.timeAny}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>{t.booking.comments} <span className="text-stone-400 font-normal">({t.common.optional})</span></label>
                  <textarea rows={3} className={inputCls} placeholder={t.booking.commentsPh} value={form.comments} onChange={(e) => upd("comments", e.target.value)} data-testid="booking-comments" />
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4" data-testid="booking-step-review">
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-4">
                  <div className="text-xs uppercase tracking-widest text-stone-400 font-semibold mb-2">{t.booking.summary}</div>
                  <dl className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><dt className="text-stone-500">{t.booking.service}</dt><dd className="font-medium text-stone-800">{serviceType.toUpperCase()}</dd></div>
                    <div className="flex justify-between"><dt className="text-stone-500">{t.booking.postcode}</dt><dd className="font-medium text-stone-800">{form.postcode}</dd></div>
                    {form.preferred_date && <div className="flex justify-between"><dt className="text-stone-500">{t.booking.preferredDate}</dt><dd className="font-medium text-stone-800">{form.preferred_date}</dd></div>}
                    <div className="flex justify-between pt-2 border-t border-stone-200 mt-2">
                      <dt className="text-stone-500">{priceData?.is_preliminary ? t.pax.preliminaryPrice : t.booking.yourPrice}</dt>
                      <dd className="font-mono font-bold text-lg text-[#1E3A2F]">
                        {isQuote ? <span className="text-sm font-sans font-medium text-stone-500">{t.booking.priceOnQuote}</span> : formatDKK(priceData?.price_incl_vat)}
                      </dd>
                    </div>
                    {!isQuote && priceData?.is_preliminary && (
                      <p className="text-xs text-amber-700 pt-1" data-testid="booking-preliminary-note">{t.pax.preliminaryNote}</p>
                    )}
                  </dl>
                </div>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="booking-consent-label">
                  <input type="checkbox" checked={form.consent} onChange={(e) => upd("consent", e.target.checked)} className="mt-1 w-4 h-4 accent-[#1E3A2F]" data-testid="booking-consent" />
                  <span className="text-sm text-stone-600 leading-relaxed">{t.booking.consentLabel}</span>
                </label>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              {step > 1 && (
                <button onClick={() => setStep((s) => s - 1)} className="flex-1 px-4 py-3 rounded-full border border-stone-300 font-semibold text-stone-700 hover:bg-stone-50 flex items-center justify-center gap-2" data-testid="booking-back-btn">
                  <ArrowLeft className="w-4 h-4" /> {t.common.back}
                </button>
              )}
              {step < 3 ? (
                <button onClick={() => { if (step === 1 && !validateStep1()) return; setStep((s) => s + 1); }} className="flex-1 px-4 py-3 rounded-full bg-[#1E3A2F] text-white font-semibold hover:bg-[#162D24] flex items-center justify-center gap-2" data-testid="booking-next-btn">
                  {t.common.next} <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={submit} disabled={submitting} className="flex-1 px-4 py-3 rounded-full bg-[#1E3A2F] text-white font-semibold hover:bg-[#162D24] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="booking-submit-btn">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.common.submitting}</> : t.common.submit}
                </button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
